import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PERMISOS_LIQUIDACION } from '../liquidacion/parametros-liquidacion/parametros-liquidacion.component';

interface ParametrosSistemaOption {
  title: string;
  description: string;
  icon: string;
  route: string;
  requiredRoles: string[];
  rbacPermissions?: string[];
  queryParams?: Record<string, string>;
  color: string; // Tailwind color class
}

@Component({
  selector: 'app-parametros-sistema-dashboard',
  templateUrl: './parametros-sistema-dashboard.component.html',
  styleUrls: []
})
export class ParametrosSistemaDashboardComponent implements OnInit {
  options: ParametrosSistemaOption[] = [
    {
      title: 'Integración SAP',
      description: 'Panel de control, alta de clientes en SAP, monitoreo (bitácora) y configuración del endpoint',
      icon: 'fas fa-cloud',
      route: '/console/sap-integracion',
      requiredRoles: ['OWNER', 'ADMIN', 'ANALIST'],
      rbacPermissions: ['config.integracion_sap.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Integración Odoo',
      description: 'Clientes y comercios anotados en Odoo, archivos del día, bitácora y configuración',
      icon: 'fas fa-link',
      route: '/console/odoo',
      requiredRoles: ['OWNER', 'ADMIN', 'ANALIST'],
      rbacPermissions: ['config.integracion_odoo.read'],
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Roles y Permisos',
      description: 'Gestión de roles RBAC, asignación de permisos y control de acceso por usuario',
      icon: 'fas fa-user-shield',
      route: '/console/roles-permisos',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.roles_permisos.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Seguridad Avanzada',
      description: 'Gestión de sesiones, 2FA, logs de auditoría y configuraciones de seguridad',
      icon: 'fas fa-shield-alt',
      route: '/console/seguridad-avanzada',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.seguridad.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    // Organización y Gestión de Comercios ya no van aquí: viven en el panel de Multicomercio
    // (Organización y Comercios). Pedido de Reybier, 2026-10-23. Las rutas siguen iguales.
    {
      // PAR-01 (Mario, 12-oct: «tenemos las cosas muy dispersas»): todo lo de liquidación en una sola opción; adentro,
      // tarifario, feriados, bandas, cuentas de origen e integraciones, cada una con su permiso.
      title: 'Liquidación a comercios',
      description: 'Tarifario, feriados bancarios, bandas de aprobación, cuentas de origen e integraciones bancarias',
      icon: 'fas fa-hand-holding-usd',
      route: '/console/parametros-sistema/liquidacion',
      requiredRoles: ['OWNER'],
      rbacPermissions: PERMISOS_LIQUIDACION,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Gestión de Tiendas',
      description: 'Administra las tiendas del sistema, configuraciones y permisos de cada sucursal',
      icon: 'fas fa-store',
      route: '/console/shops',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.tiendas.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Gestión de Métodos de pagos',
      description: 'Configura y administra los métodos de pago disponibles en el sistema',
      icon: 'fas fa-credit-card',
      route: '/console/payment-options',
      requiredRoles: ['OWNER', 'ANALIST'],
      rbacPermissions: ['config.metodos_pago.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Cuentas por contrato',
      description: 'Qué cuentas ve el cliente en cada opción de pago, con cada contrato bancario (MT y CT)',
      icon: 'fas fa-university',
      route: '/console/perfil-bancario',
      queryParams: { pestana: 'cuentas' },
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.perfil_bancario.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Productos',
      description: 'Configura categorías de productos y gestiona productos disponibles para financiamiento',
      icon: 'fas fa-box',
      route: '/console/type_product',
      requiredRoles: ['OWNER', 'ANALIST'],
      rbacPermissions: ['config.productos.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Planes',
      description: 'Configura planes de crédito, límites, condiciones y opciones de financiamiento',
      icon: 'fas fa-clipboard-list',
      route: '/console/plans',
      requiredRoles: ['OWNER', 'ANALIST'],
      rbacPermissions: ['config.planes.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Etiquetas',
      description: 'Administra etiquetas del sistema, códigos dinámicos y filtros de categorización',
      icon: 'fas fa-tags',
      route: '/console/codigos',
      requiredRoles: ['OWNER', 'ANALIST'],
      rbacPermissions: ['config.etiquetas.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Comisiones y Penalidades',
      description: 'Administra y configura las comisiones y penalidades aplicables a los diferentes planes de financiamiento',
      icon: 'fas fa-percentage',
      route: '/console/fees',
      requiredRoles: ['OWNER', 'ANALIST'],
      rbacPermissions: ['config.comisiones.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Gestión de Notificaciones',
      description: 'Administra plantillas, historial y configuraciones de notificaciones',
      icon: 'fas fa-comments',
      route: '/console/notifications',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.notificaciones.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Tasa de Cambio',
      description: 'Administra las tasas de cambio y conversiones monetarias del sistema',
      icon: 'fas fa-exchange-alt',
      route: '/console/rates',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.tasas.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Administración de Pagos Asíncronos',
      description: 'Monitorea y administra el sistema de verificación automática de pagos',
      icon: 'fas fa-sync-alt',
      route: '/console/async-admin',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.pagos_async.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Consola Administrativa de Seguros',
      description: 'Dashboard completo de métricas, analytics y gestión del sistema de seguros',
      icon: 'fas fa-shield-alt',
      route: '/console/parametros-sistema/insurance-console',
      requiredRoles: ['OWNER', 'ANALIST'],
      rbacPermissions: ['config.seguros.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Configuración de Aplicación Móvil',
      description: 'Administra servicios disponibles y configuraciones de la aplicación móvil',
      icon: 'fas fa-mobile-alt',
      route: '/console/parametros-sistema/app-config',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.app_movil.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Clientes con App',
      description: 'Clientes que usan la app nueva, con la versión que tienen instalada, canal y estado de sesión',
      icon: 'fas fa-code-branch',
      route: '/console/app-clients',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.clientes_app.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Procesos Masivos',
      description: 'Monitoreo y trazabilidad de ejecuciones masivas (mora, fees, cron jobs)',
      icon: 'fas fa-tasks',
      route: '/console/procesos-masivos',
      requiredRoles: ['OWNER', 'ADMIN', 'ANALIST'],
      rbacPermissions: ['config.procesos_masivos.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Categorías de Desactivación',
      description: 'Agrupa motivos de desactivación de cupo y define el mensaje que verá el cliente en la app',
      icon: 'fas fa-user-slash',
      route: '/console/parametros-sistema/deactivation-categories',
      requiredRoles: ['OWNER'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Preferencias',
      description: 'Configura preferencias de la consola, como la vista de pagos por defecto (tabla o clásica)',
      icon: 'fas fa-sliders-h',
      route: '/console/parametros-sistema/preferencias',
      requiredRoles: ['OWNER'],
      rbacPermissions: ['config.preferencias.read'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Cartera Castigada (Pérdidas Incobrables)',
      description: 'Consulta y gestión de créditos, cuotas y clientes clasificados como pérdida incobrable',
      icon: 'fas fa-file-invoice-dollar',
      route: '/console/parametros-sistema/cartera-castigada',
      requiredRoles: ['OWNER'],
      // Sin esto, hasPermission() caía al fallback hasModuleAccess('config') y el módulo
      // se mostraba aunque el permiso estuviera DENEGADO. Con el permiso específico, respeta el deny.
      rbacPermissions: ['config.cartera_castigada.read'],
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  userRoles: string[] = [];

  constructor(public _auth: AuthService) { }

  ngOnInit(): void {
    this.userRoles = Array.isArray(this._auth.sessionUser?.profile) ? this._auth.sessionUser.profile : [];
  }

  hasPermission(option: ParametrosSistemaOption): boolean {
    if (this._auth.hasRbac$.getValue()) {
      if (option.rbacPermissions && option.rbacPermissions.length > 0) {
        return this._auth.hasAnyPermission(option.rbacPermissions);
      }
      return this._auth.hasModuleAccess('config');
    }
    if (!option.requiredRoles || option.requiredRoles.length === 0) return true;
    return option.requiredRoles.some(role => this.userRoles.includes(role));
  }
}
